import {EventListeners, MinzeElement, Reactive} from "minze";
import {config} from "../../constants";
import {Locale} from "../../modules/locale";
import { SquidexTextLabels } from "../../modules/squidex/labels";

export interface TjPodcasts {
  podcasts: any[],
  page: number,
  openedVideo: string
}

Locale.getInstance().registerKeySet("fr", "podcasts", {
  "pageTitle": "Tous nos balados",
  "season": "Saison",
  "episode": "Episode",
  "filtersLabel": "Toutes les saisons"
});

Locale.getInstance().registerKeySet("en", "podcasts", {
  "pageTitle": "All our podcasts",
  "season": "Season",
  "episode": "Episode",
  "filtersLabel": "All Seasons"
});

export class TjPodcasts extends MinzeElement {
  reactive: Reactive = [
    ["podcasts", []],
    ["page", 1],
    ["openedVideo", ""]
  ];
  lang = Locale.language;
  itemsPerPage = 12;
  podcastsData = [];
  season: number | undefined = undefined;
  isMobile = window.matchMedia("(max-width: 768px)").matches

  public async loadPodcasts() {
    const api = `${config.apiRoute}/aproposBalados`
    const response = await fetch(api);
    const podcasts = await response.json();
    this.podcasts = podcasts;
    this.podcastsData = podcasts;
    this.rerender();
  }

  changePage = (event: Event) => {
    const dateCard = (event.target as Element)?.closest('.pagination-button') as Element | null;
    const totalPages = Math.ceil(this.podcasts.length / this.itemsPerPage);
    const page = parseInt(dateCard?.getAttribute("data-page") as string);
    if (page && page <= totalPages) this.page = page;
  }

  renderPagination = () => {
    const renderButton = (page: number) => {
      return `
        <button class="${page === (this.page) ? "active" : ""} pagination-button" data-page="${page}">
          ${page}
        </button>
      `
    }

    const renderEllipsis = () => `<button class="pagination-button ellipsis">...</button>`

    const totalPages = Math.ceil(this.podcasts.length / this.itemsPerPage);
    const buttonArray = Array.from(
      { length: totalPages > 4 ? 5 : totalPages},
      (_, index) => index + 1
    );

    let pagination = ``

    if (totalPages > 5) {
      const currentPage = this.page;
      if (currentPage + 2 >= totalPages - 1) {
        pagination = pagination + renderButton(1);
        pagination = pagination + renderEllipsis();
      }
      const isCurrentPageFirst = currentPage === 0;
      const isCurrentPageLast = currentPage + 1 === totalPages;
      for (let i = 1; i <= 3; i++) {
        const page = currentPage - (
          isCurrentPageLast ? 2 :
            !isCurrentPageFirst ? 1 : 0
        ) + i;
        if (page <= totalPages) pagination = pagination + renderButton(page);
      }
      if (currentPage + 2 < totalPages) {
        if (currentPage + 2 < totalPages - 1) pagination = pagination + renderEllipsis();
        pagination = pagination + renderButton(totalPages);
      }
    } else {
      pagination = buttonArray.map((button) => `${renderButton(button)}`).join("")
    }
    return `
      <div class="pagination">
        <button class="prev pagination-button" data-page="${this.page - 1}">
          <tj-pagination-prev-icon></tj-pagination-prev-icon>
        </button>
        ${pagination}
        <button class="next pagination-button" data-page="${this.page + 1}">
          <tj-pagination-next-icon></tj-pagination-next-icon>
        </button>
      </div>
    `
  }

  onReady() {
    this.loadPodcasts()
  }

  renderFilters() {
    const seasons: number[] = []
    this.podcastsData.forEach((podcast: any) => {
      if (!seasons.includes(podcast.data.season.iv)) seasons.push(podcast.data.season.iv);
    })
    seasons.sort()
    return `
      <tj-select
        id="filters"
        name="filters"
        placeholder="${SquidexTextLabels.getKey("podcasts.filtersLabel")}"
        default-value="${this.season || ""}"
        selectOptions="${[[...seasons]].join(",")}"
        selectOptionLabels="${seasons.map(season => `${SquidexTextLabels.getKey("podcasts.season")} ${season}`).join(",")}"
        value="${this.season}"
      ></tj-select>
    `
  }

  changeFilter(event: Event) {
    const detail = (event as CustomEvent<{ value: string }>).detail
    const selectedSeason = parseInt(detail.value);
    if (!selectedSeason) {
      this.podcasts = this.podcastsData;
      this.season = undefined;
      this.rerender();
    } else {
      this.page = 1;
      this.podcasts = this.podcastsData?.filter((podcast: any) => podcast.data.season.iv === selectedSeason)
      this.season = selectedSeason;
      this.rerender();
    }
  }

  closeVideo() {
    this.openedVideo = "";
  }

  openVideo(event: Event) {
    const target = (event.target as HTMLElement)?.closest('.poster') as Element | null;
    const video = target?.getAttribute("data-video");
    if (video) this.openedVideo = video;
  }

  formatDate(input: string): string {
    let date = new Date(input);
    return date.toLocaleDateString(this.lang === "en" ? 'en-EN' : 'fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  }

  html = () => /* html */ `
    ${this.openedVideo ? `
      <div class="fullscreen-video">
        <button class="close-button">
          <tj-close-icon></tj-close-icon>
        </button>
        <iframe src="${this.openedVideo}" allowfullscreen="allowfullscreen"></iframe>
      </div>
    ` : ""}
    <tj-box 
      mb="48px" 
      display="flex" 
      alignItems="${this.isMobile ? "flex-end" : "center"}" 
      justifyContent="${this.isMobile ? "flex-end" : "space-between"}"
      flexDirection="${this.isMobile ? "column" : "row"}"
    >
      <tj-text font-weight="500" line-height="125%" font-size="34px" color="#068FC3" class="page-title">
        ${SquidexTextLabels.getKey("podcasts.pageTitle")}
      </tj-text>
      ${this.renderFilters()}
    </tj-box>
    <div class="podcasts-grid">
      ${this.podcasts.slice((this.page - 1) * this.itemsPerPage, this.itemsPerPage * this.page).map(podcast => `
        <div class="podcast">
          <div
            class="poster"
            data-video="${podcast.data.embedSrc[this.lang]}"
            style="background-image: url(${podcast.data.thumbnail[this.lang]})"
          >
            <tj-yt-play-icon></tj-yt-play-icon>
          </div>
          <tj-box display="flex" justifyContent="space-between" mt="8px" mb="4px" height="auto">
            <div class="podcast-info">
              ${SquidexTextLabels.getKey("podcasts.season")} ${podcast.data.season?.iv} - ${SquidexTextLabels.getKey("podcasts.episode")} ${podcast.data.episode?.iv}
            </div>
            <div class="podcast-date">${this.formatDate(podcast.data.publishDate?.iv)}</div>
          </tj-box>
          <h3>${podcast.data.title[this.lang]}</h3>
        </div>
      `).join("")}
    </div>
    ${this.renderPagination()}
  `;

  css = () => `
    .podcast {
      width: calc(33.33% - 16px);
    }
    .poster {
      padding: 90px 0;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 16px;
      cursor: pointer;
      background-size: cover;
    }
    .podcast-info {
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%;
      text-transform: uppercase;
      color: #068FC3
    }
    .podcast-date {
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%;
      text-transform: uppercase;
      color: #133762;
    }
    .podcast h3 {
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%;
      color: #133762;
      margin: 0;
    }
    .podcasts-grid {
      display: flex;
      flex-wrap: wrap;
      row-gap: 40px;
      column-gap: 24px;
    }
    .pagination-button {
      font-weight: 700;
      font-size: 14px;
      line-height: 150%;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      text-transform: uppercase;
      color: #133762;
      border-radius: 8px;
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      cursor: pointer;
    }
    .pagination-button.active {
      background: #068FC3;
      color: #FFF;
    }
    .pagination {
      display: flex;
      justify-content: center;
    }
    tj-pagination-prev-icon, tj-pagination-next-icon {
      width: 24px;
      height: 24px;
    }
    .pagination {
      margin-top: 24px;
    }
    .fullscreen-video {
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background: #00000090;
      padding: 80px;
      text-align: center;
      z-index: 1;
    }
    .fullscreen-video iframe {
      border: none;
      width: 100%;
      max-width: 1280px;
      aspect-ratio: 16/9;
      max-height: 70vh;
      border-radius: 8px;
    }
    .fullscreen-video .close-button {
      background: none;
      border: none;
      cursor: pointer;
      margin-bottom: 40px;
      width: 100%;
      text-align: right;
      max-width: 1280px;
    }
    #filters {
      width: 200px;
    }
    @media (max-width: 768px) {
      .podcast {
        width: 100%;
      }
      .page-title {
        width: 100%;
        text-align: center;
        margin-bottom: 24px;
      }
      .fullscreen-video {
        padding: 25px;
      }
      .fullscreen-video iframe {
        height: 200px;
        margin-top: 20%;
      }
    }
  `

  eventListeners: EventListeners = [
    [".pagination-button", "click", this.changePage],
    [".close-button", "click", this.closeVideo.bind(this)],
    [".fullscreen-video", "click", this.closeVideo.bind(this)],
    [".poster", "click", this.openVideo.bind(this)],
    [window, "tj:select-value-change-filters", this.changeFilter.bind(this)]
  ];
}
